// frontend/src/features/equipment/EquipmentSetupWizard/steps/CategoryStep.tsx
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useSubcategories } from '@/hooks/useEquipmentCatalog';
import { SubcategorySection } from '../components/SubcategorySection';
import type { PendingEquipment } from '../types';

interface CategoryStepProps {
  categoryId: string;
  title: string;
  isCategoryComplete?: boolean;
  pendingEquipment: PendingEquipment[];
  onEquipmentChange: (equipment: PendingEquipment[]) => void;
  onManageInEquipment?: () => void;
}

export function CategoryStep({
  categoryId,
  title,
  isCategoryComplete = false,
  pendingEquipment,
  onEquipmentChange,
  onManageInEquipment,
}: CategoryStepProps) {
  const subcategories = useSubcategories(categoryId);
  const [showForm, setShowForm] = useState(!isCategoryComplete);

  const hasCategoryDrafts = pendingEquipment.some((equipment) =>
    subcategories.some((subcategory) => subcategory.id === equipment.subcategoryId)
  );
  const isFormVisible = showForm || hasCategoryDrafts;

  useEffect(() => {
    setShowForm(!isCategoryComplete);
  }, [isCategoryComplete]);

  useEffect(() => {
    if (hasCategoryDrafts && !showForm) {
      setShowForm(true);
    }
  }, [hasCategoryDrafts, showForm]);

  /**
   * Handle equipment changes from a specific subcategory.
   * We need to merge the changes with equipment from other subcategories.
   */
  const handleSubcategoryEquipmentChange = (
    subcategoryId: string,
    newEquipment: PendingEquipment[]
  ) => {
    // Remove all equipment for this subcategory, then add the new ones
    const otherEquipment = pendingEquipment.filter(
      (eq) => eq.subcategoryId !== subcategoryId
    );
    onEquipmentChange([...otherEquipment, ...newEquipment]);
  };

  /**
   * Get equipment for a specific subcategory.
   */
  const getEquipmentForSubcategory = (subcategoryId: string): PendingEquipment[] => {
    return pendingEquipment.filter((eq) => eq.subcategoryId === subcategoryId);
  };

  return (
    <Stack gap="lg" data-testid={`category-step-${categoryId}`}>
      <Title order={4}>{title}</Title>

      {isCategoryComplete && !isFormVisible && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Stack gap="sm">
            <Text size="sm">You already have equipment in this category.</Text>
            <Group gap="sm">
              <Button size="xs" variant="light" onClick={() => setShowForm(true)}>
                Add more
              </Button>
              <Button
                size="xs"
                variant="subtle"
                onClick={onManageInEquipment}
              >
                Manage in Equipment
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {isFormVisible &&
        subcategories.map((subcategory) => (
          <SubcategorySection
            key={subcategory.id}
            subcategory={subcategory}
            equipment={getEquipmentForSubcategory(subcategory.id)}
            onEquipmentChange={(newEquipment) =>
              handleSubcategoryEquipmentChange(subcategory.id, newEquipment)
            }
          />
        ))}
    </Stack>
  );
}
